import React, { useState } from 'react';
import { Camera, Upload, Loader2, Apple, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';

const NutritionAnalyzer = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setAnalysis(null);
      setError('');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/analyze-nutrition`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Apple className="w-8 h-8 text-cyan-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">AI Nutrition Analyzer</h2>
          <p className="text-gray-400">Take a photo of your meal for instant analysis</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <div className="flex flex-col items-center justify-center">
          {imagePreview ? (
            <div className="w-full max-w-md">
              <img src={imagePreview} alt="Meal" className="w-full rounded-lg mb-4" />
              <div className="flex gap-3">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button className="w-full bg-gray-700 hover:bg-gray-600">
                    <Upload className="w-4 h-4 mr-2" />
                    Change Image
                  </Button>
                </label>
                <Button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Analyze
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Camera className="w-12 h-12 text-gray-400 mb-3" />
                <p className="mb-2 text-lg font-semibold text-white">Upload Meal Photo</p>
                <p className="text-sm text-gray-400">Click to browse or drag and drop</p>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG or JPEG (MAX. 10MB)</p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-6">
          <h3 className="text-2xl font-bold text-white">Analysis Results</h3>

          {/* Food Items */}
          <div>
            <h4 className="text-lg font-semibold text-cyan-400 mb-3">Detected Foods</h4>
            <div className="flex flex-wrap gap-2">
              {analysis.foods && analysis.foods.map((food, idx) => (
                <span key={idx} className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-sm text-cyan-400">
                  {food}
                </span>
              ))}
            </div>
          </div>

          {/* Macros */}
          <div>
            <h4 className="text-lg font-semibold text-cyan-400 mb-3">Nutritional Breakdown</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-400 mb-1">Calories</p>
                <p className="text-2xl font-bold text-white">{analysis.calories}</p>
                <p className="text-xs text-gray-400">kcal</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <p className="text-sm text-red-400 mb-1">Protein</p>
                <p className="text-2xl font-bold text-white">{analysis.protein}</p>
                <p className="text-xs text-gray-400">g</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <p className="text-sm text-yellow-400 mb-1">Carbs</p>
                <p className="text-2xl font-bold text-white">{analysis.carbs}</p>
                <p className="text-xs text-gray-400">g</p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-sm text-green-400 mb-1">Fats</p>
                <p className="text-2xl font-bold text-white">{analysis.fats}</p>
                <p className="text-xs text-gray-400">g</p>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          {analysis.insights && (
            <div>
              <h4 className="text-lg font-semibold text-cyan-400 mb-3">AI Insights</h4>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-gray-300 whitespace-pre-line">{analysis.insights}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NutritionAnalyzer;
